import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../../lib/api.js';

function fmt(dateStr) {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function truncate(str, n) {
  if (!str) return '—';
  return str.length > n ? str.slice(0, n) + '…' : str;
}

export default function Prompts() {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/dev/prompts');
      setPrompts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      await api.put(`/dev/prompts/${encodeURIComponent(name.trim())}`, {
        content,
        notes: notes.trim() || undefined,
      });
      setName('');
      setContent('');
      setNotes('');
      setShowCreate(false);
      await load();
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const createForm = (
    <form onSubmit={handleCreate} className="space-y-4">
      <div>
        <label className="hxd-label">Name</label>
        <input
          className="hxd-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. stage_2_classify"
          required
        />
      </div>
      <div>
        <label className="hxd-label">Content</label>
        <textarea
          className="hxd-input font-mono text-[13px]"
          rows={6}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Prompt text…"
          required
        />
      </div>
      <div>
        <label className="hxd-label">
          Notes{' '}
          <span className="font-normal normal-case tracking-normal text-hxd-muted">
            (optional)
          </span>
        </label>
        <input
          className="hxd-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Initial version"
        />
      </div>
      {createError && (
        <p className="text-hxd-err text-[13px]">{createError}</p>
      )}
      <button
        type="submit"
        className="hxd-btn hxd-btn-primary hxd-btn-sm"
        disabled={creating || !name.trim() || !content.trim()}
      >
        {creating ? 'Creating…' : 'Create prompt'}
      </button>
    </form>
  );

  if (loading) {
    return (
      <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
        <Link
          to="/admin/dev"
          className="text-[12px] text-hxd-muted hover:text-hxd-orange transition-colors"
        >
          ← Back to Dev Dashboard
        </Link>
        <p className="text-hxd-muted text-[13px] mt-4">Loading prompts…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
        <Link
          to="/admin/dev"
          className="text-[12px] text-hxd-muted hover:text-hxd-orange transition-colors"
        >
          ← Back to Dev Dashboard
        </Link>
        <p className="text-hxd-err text-[13px] mt-4">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
      <Link
        to="/admin/dev"
        className="text-[12px] text-hxd-muted hover:text-hxd-orange transition-colors"
      >
        ← Back to Dev Dashboard
      </Link>
      <div className="mb-6 mt-4">
        <h1 className="hxd-page-title">Prompts</h1>
        <p className="hxd-page-subtitle">Active version of each pipeline prompt.</p>
      </div>

      {prompts.length === 0 ? (
        <div className="hxd-card">
          <div className="hxd-card-header">No prompts yet — create the first one</div>
          {createForm}
        </div>
      ) : (
        <>
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            className="hxd-btn hxd-btn-primary hxd-btn-sm"
            onClick={() => setShowCreate((v) => !v)}
          >
            {showCreate ? 'Cancel' : '+ New prompt'}
          </button>
        </div>
        {showCreate && (
          <div className="hxd-card mb-4">
            <div className="hxd-card-header">New prompt</div>
            {createForm}
          </div>
        )}
        <div className="hxd-card p-0 overflow-hidden">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b-2 border-hxd-orange bg-hxd-surface">
                <th className="font-display text-[10px] font-bold tracking-display-md uppercase text-left px-5 py-3">
                  Name
                </th>
                <th className="font-display text-[10px] font-bold tracking-display-md uppercase text-left px-5 py-3">
                  Version
                </th>
                <th className="font-display text-[10px] font-bold tracking-display-md uppercase text-left px-5 py-3 hidden sm:table-cell">
                  Updated
                </th>
                <th className="font-display text-[10px] font-bold tracking-display-md uppercase text-left px-5 py-3 hidden md:table-cell">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody>
              {prompts.map((p) => (
                <tr
                  key={p.name}
                  className="border-b border-hxd-border last:border-0 hover:bg-hxd-surface transition-colors"
                >
                  <td className="px-5 py-3">
                    <Link
                      to={`/admin/dev/prompts/${encodeURIComponent(p.name)}`}
                      className="font-semibold text-hxd-ink hover:text-hxd-orange transition-colors"
                    >
                      {p.name}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-hxd-muted">v{p.version}</td>
                  <td className="px-5 py-3 text-hxd-muted hidden sm:table-cell">
                    {fmt(p.updatedAt)}
                  </td>
                  <td className="px-5 py-3 text-hxd-muted hidden md:table-cell">
                    {truncate(p.notes, 60)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
